/**
 * A side sheet for the player (plan docs/plans/lms-plan.md 7.2 "a section
 * list drawer"; 6.1 the glossary "as a side sheet in the player"). The design
 * skill's drawer shape: fixed, w-72 max-w-[85vw], a bg-black/50 overlay;
 * Escape and an overlay press close it.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

export interface DrawerProps {
  open: boolean;
  side: 'left' | 'right';
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}

export function Drawer({ open, side, title, onClose, children, testId }: DrawerProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex" data-lms-drawer="open">
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" onMouseDown={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        className={`relative flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-xl dark:bg-gray-800 ${side === 'right' ? 'ml-auto' : ''}`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
      </aside>
    </div>
  );
}

export default Drawer;
